import { createFileRoute } from '@tanstack/react-router';
import { Download } from 'lucide-react';
import React, { type ReactNode, useState } from 'react';

import { CategoryBarChart } from '@apps/web/components/admin/org_distributions/category_bar_chart';
import { DistributionHistogram } from '@apps/web/components/admin/org_distributions/distribution_histogram';
import { GeographicMap } from '@apps/web/components/admin/org_distributions/geographic_map';
import {
    type DistancePopulation,
    RecenteredScatter,
} from '@apps/web/components/admin/org_distributions/recentered_scatter';
import { OrganizationCombobox } from '@apps/web/components/admin/organization_combobox';
import { Button } from '@apps/web/components/ui/button';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@apps/web/components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '@apps/web/components/ui/tabs';
import { toast } from '@apps/web/lib/app_notifications';
import {
    useAdminOrgDistributionsMap,
    useAdminOrgDistributionsStatic,
    useAdminOrgDistributionsWindowed,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';
import {
    type components,
    type CountDistribution,
    type DistanceDistribution,
    type DistributionWindow,
    type Mode,
    type OrgDistributionsStaticResponse,
    type OrgDistributionsWindowedResponse,
} from '@libraries/typescript/api_client';

export const Route = createFileRoute('/_app/admin/org-distributions/')({
    component: OrgDistributionsPage,
});

type OrgDistributionsMapResponse =
    components['schemas']['OrgDistributionsMapResponse'];

function OrgDistributionsPage() {
    const [organizationId, setOrganizationId] = useState<string | null>(null);
    const [mode, setMode] = useState<Mode>('live');
    const [window, setWindow] = useState<DistributionWindow>('last_7_days');
    const [distancePopulation, setDistancePopulation] =
        useState<DistancePopulation>('both');
    const {
        data: staticData,
        error: staticError,
        isLoading: isLoadingStatic,
    } = useAdminOrgDistributionsStatic(organizationId, mode);
    const {
        data: windowedData,
        error: windowedError,
        isLoading: isLoadingWindowed,
    } = useAdminOrgDistributionsWindowed(organizationId, mode, window);
    const {
        data: mapData,
        error: mapError,
        isLoading: isLoadingMap,
    } = useAdminOrgDistributionsMap(organizationId, mode, window);
    const canExportJson =
        !!organizationId &&
        !!staticData &&
        !!windowedData &&
        !!mapData &&
        !staticError &&
        !windowedError &&
        !mapError &&
        !isLoadingStatic &&
        !isLoadingWindowed &&
        !isLoadingMap;

    function handleExportJson() {
        if (!organizationId || !staticData || !windowedData || !mapData) {
            return;
        }

        try {
            const exportedAt = new Date().toISOString();
            const payload: OrgDistributionsJsonExport = {
                export_version: 1,
                exported_at: exportedAt,
                organization_id: organizationId,
                mode,
                window,
                static_distributions: staticData,
                windowed_distributions: windowedData,
                map_distribution: mapData,
            };
            const timestamp = exportedAt.replace(/[:.]/g, '-');
            downloadJsonFile(
                payload,
                `org-distributions-${organizationId}-${mode}-${window}-${timestamp}.json`,
            );
        } catch {
            toast.error('JSON export failed', {
                description: 'Please try again.',
                persistNotification: false,
            });
        }
    }

    return (
        <div className="flex flex-col gap-4 p-4 md:p-6">
            <div className="flex flex-wrap items-end gap-3">
                <div className="min-w-72">
                    <div className="text-muted-foreground mb-1 text-xs font-medium">
                        Organization
                    </div>
                    <OrganizationCombobox
                        value={organizationId ? [organizationId] : []}
                        onValueChange={(ids) => {
                            setOrganizationId(
                                ids.find((id) => id !== organizationId) ?? null,
                            );
                        }}
                        placeholder="Select organization"
                    />
                </div>
                <div>
                    <div className="text-muted-foreground mb-1 text-xs font-medium">
                        Mode
                    </div>
                    <Tabs
                        value={mode}
                        onValueChange={(value) => {
                            setMode(value as Mode);
                        }}
                    >
                        <TabsList>
                            <TabsTrigger value="live">Live</TabsTrigger>
                            <TabsTrigger value="sandbox">Sandbox</TabsTrigger>
                        </TabsList>
                    </Tabs>
                </div>
                <div>
                    <div className="text-muted-foreground mb-1 text-xs font-medium">
                        Window
                    </div>
                    <Tabs
                        value={window}
                        onValueChange={(value) => {
                            setWindow(value as DistributionWindow);
                        }}
                    >
                        <TabsList>
                            <TabsTrigger value="last_7_days">7d</TabsTrigger>
                            <TabsTrigger value="last_30_days">30d</TabsTrigger>
                            <TabsTrigger value="last_90_days">90d</TabsTrigger>
                            <TabsTrigger value="all_time">All time</TabsTrigger>
                        </TabsList>
                    </Tabs>
                </div>
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={!canExportJson}
                    onClick={handleExportJson}
                    className="gap-2"
                >
                    <Download className="h-4 w-4" />
                    Export JSON
                </Button>
            </div>
            {!organizationId ? (
                <EmptyPanel>
                    Select an organization to view distributions.
                </EmptyPanel>
            ) : staticError ? (
                <EmptyPanel>Unable to load distributions.</EmptyPanel>
            ) : isLoadingStatic || !staticData ? (
                <EmptyPanel>Loading…</EmptyPanel>
            ) : (
                <div className="space-y-4">
                    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                        <SummaryCard title="Candidate Pool">
                            <MetricLine
                                label="Active caregivers"
                                value={
                                    staticData.candidate_pool.active_caregivers
                                }
                            />
                            <MetricLine
                                label="Inactive caregivers"
                                value={
                                    staticData.candidate_pool
                                        .inactive_caregivers
                                }
                            />
                            <MetricLine
                                label="Active clients"
                                value={staticData.candidate_pool.active_clients}
                            />
                            <MetricLine
                                label="Inactive clients"
                                value={
                                    staticData.candidate_pool.inactive_clients
                                }
                            />
                        </SummaryCard>
                        <SummaryCard title="Geocode Coverage">
                            <MetricLine
                                label="Caregivers"
                                value={coverageText(
                                    staticData.geocode_coverage
                                        .caregiver_geocoded,
                                    staticData.geocode_coverage.caregiver_total,
                                )}
                            />
                            <MetricLine
                                label="Clients"
                                value={coverageText(
                                    staticData.geocode_coverage.client_geocoded,
                                    staticData.geocode_coverage.client_total,
                                )}
                            />
                        </SummaryCard>
                    </div>
                    <div className="grid gap-4 xl:grid-cols-2">
                        <div className="xl:col-span-2">
                            <ChartCard
                                title="Caregiver and Client Map"
                                description="Geocoded caregivers, clients, and shift links for the selected window."
                            >
                                <GeographicMap
                                    data={mapData}
                                    hasError={!!mapError}
                                    isLoading={isLoadingMap}
                                />
                            </ChartCard>
                        </div>
                        <ChartCard
                            title="Caregivers Per Tag"
                            description="Active caregivers grouped by raw tag values."
                        >
                            <CategoryBarChart
                                data={staticData.caregivers_per_tag}
                            />
                        </ChartCard>
                        <ChartCard
                            title="Clients Per Tag"
                            description="Active clients grouped by raw tag values."
                        >
                            <CategoryBarChart
                                data={staticData.clients_per_tag}
                            />
                        </ChartCard>
                        <CountDistributionCard
                            title="Memories Per Caregiver"
                            description="Memory fragments available to the outreach agent."
                            distribution={staticData.memories_per_caregiver}
                        />
                        <CountDistributionCard
                            title="Contact Attempts Per Caregiver"
                            description="Recent composer saturation signal over the last 48 hours."
                            distribution={
                                staticData.contact_attempts_48h_per_caregiver
                            }
                        />
                        <CountDistributionCard
                            title="Preferences Per Caregiver"
                            description="All caregiver-client preference rows for active entities."
                            distribution={staticData.preferences_per_caregiver}
                        />
                        <CountDistributionCard
                            title="Preferences Per Client"
                            description="Client-side concentration of stored preference rows."
                            distribution={staticData.preferences_per_client}
                        />
                        <ChartCard
                            title="Preference Breakdown"
                            description="Preference rows grouped by recommendation meaning."
                        >
                            <CategoryBarChart
                                data={staticData.preference_breakdown_by_value}
                            />
                        </ChartCard>
                        <CountDistributionCard
                            title="Weekly Availability Blocks Per Caregiver"
                            description="Recurring availability and unavailability blocks on active caregivers."
                            distribution={
                                staticData.weekly_availability_per_caregiver
                            }
                        />
                        <CountDistributionCard
                            title="Weekly Available Hours Per Caregiver"
                            description="Recurring weekly available hours on active caregivers."
                            distribution={
                                staticData.weekly_available_hours_per_caregiver
                            }
                        />
                        <ChartCard
                            title="Geographic Spread"
                            description="Cached caregiver and client coordinates recentered in miles."
                        >
                            <GeographicClusterSummary
                                distance={staticData.distance}
                            />
                            <RecenteredScatter
                                distance={staticData.distance}
                                population={distancePopulation}
                                onPopulationChange={setDistancePopulation}
                            />
                        </ChartCard>
                        <WindowedDistributionCards
                            windowedData={windowedData}
                            isLoading={isLoadingWindowed}
                            hasError={!!windowedError}
                        />
                    </div>
                </div>
            )}
        </div>
    );
}

function GeographicClusterSummary({
    distance,
}: {
    readonly distance: DistanceDistribution;
}) {
    if (
        distance.cluster_selection === null ||
        distance.cluster_selection.chosen_k <= 1
    ) {
        return null;
    }

    const clusterCounts = distance.spatial_clusters
        .map(
            (cluster, index) =>
                `C${index + 1}: ${cluster.caregivers.count} caregivers / ${
                    cluster.clients.count
                } clients`,
        )
        .join('; ');

    return (
        <div className="text-muted-foreground mb-3 text-xs">
            {distance.cluster_selection.chosen_k} geographic clusters detected
            (centers marked): {clusterCounts}
        </div>
    );
}

type OrgDistributionsJsonExport = {
    readonly export_version: 1;
    readonly exported_at: string;
    readonly organization_id: string;
    readonly mode: Mode;
    readonly window: DistributionWindow;
    readonly static_distributions: OrgDistributionsStaticResponse;
    readonly windowed_distributions: OrgDistributionsWindowedResponse;
    readonly map_distribution: OrgDistributionsMapResponse;
};

function WindowedDistributionCards({
    windowedData,
    isLoading,
    hasError,
}: {
    readonly windowedData: OrgDistributionsWindowedResponse | undefined;
    readonly isLoading: boolean;
    readonly hasError: boolean;
}) {
    if (hasError) {
        return (
            <ChartCard
                title="Windowed Distributions"
                description="Metrics for the selected time window."
            >
                <EmptyPanel>Unable to load windowed distributions.</EmptyPanel>
            </ChartCard>
        );
    }

    if (isLoading || !windowedData) {
        return (
            <ChartCard
                title="Windowed Distributions"
                description="Metrics for the selected time window."
            >
                <EmptyPanel>Loading…</EmptyPanel>
            </ChartCard>
        );
    }

    return (
        <React.Fragment>
            <CountDistributionCard
                title="Shifts Per Caregiver"
                description="Active-status shifts assigned to each active caregiver."
                distribution={windowedData.shifts_per_caregiver}
            />
            <CountDistributionCard
                title="Hours Per Caregiver"
                description="Scheduled shift hours for each active caregiver."
                distribution={windowedData.hours_per_caregiver}
            />
            <CountDistributionCard
                title="Shifts Per Client"
                description="Active-status shifts for each active client."
                distribution={windowedData.shifts_per_client}
            />
            <CountDistributionCard
                title="Unique Clients Per Caregiver"
                description="Distinct active clients each caregiver has served."
                distribution={windowedData.unique_clients_per_caregiver}
            />
            <CountDistributionCard
                title="Previous Visits Per Pair"
                description="Visit counts for active caregiver-client pairs."
                distribution={windowedData.previous_visits_per_pair}
            />
            <ChartCard
                title="Shifts By Status"
                description="All shift statuses in the selected window."
            >
                <CategoryBarChart data={windowedData.shifts_by_status} />
            </ChartCard>
            <CountDistributionCard
                title="One-Time Availability Per Caregiver"
                description="Availability intervals overlapping the selected window."
                distribution={windowedData.availability_per_caregiver}
            />
        </React.Fragment>
    );
}

function EmptyPanel({ children }: { readonly children: ReactNode }) {
    return (
        <div className="text-muted-foreground flex min-h-80 items-center justify-center rounded-md border text-sm">
            {children}
        </div>
    );
}

function SummaryCard({
    title,
    children,
}: {
    readonly title: string;
    readonly children: ReactNode;
}) {
    return (
        <Card>
            <CardHeader>
                <CardTitle>{title}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">{children}</CardContent>
        </Card>
    );
}

function MetricLine({
    label,
    value,
}: {
    readonly label: string;
    readonly value: string | number;
}) {
    return (
        <div className="flex items-center justify-between gap-3">
            <span className="text-muted-foreground">{label}</span>
            <span className="font-medium tabular-nums">{value}</span>
        </div>
    );
}

function ChartCard({
    title,
    description,
    children,
}: {
    readonly title: string;
    readonly description: string;
    readonly children: ReactNode;
}) {
    return (
        <Card>
            <CardHeader>
                <CardTitle>{title}</CardTitle>
                <CardDescription>{description}</CardDescription>
            </CardHeader>
            <CardContent>{children}</CardContent>
        </Card>
    );
}

function CountDistributionCard({
    title,
    description,
    distribution,
}: {
    readonly title: string;
    readonly description: string;
    readonly distribution: CountDistribution;
}) {
    return (
        <ChartCard
            title={title}
            description={`${description} ${distribution.nonzero_entity_count} of ${distribution.entity_total} have data.`}
        >
            <DistributionHistogram bins={distribution.bins} />
        </ChartCard>
    );
}

function downloadJsonFile(value: object, filename: string) {
    const blob = new Blob([JSON.stringify(value, null, 2)], {
        type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    try {
        anchor.click();
    } finally {
        document.body.removeChild(anchor);
        URL.revokeObjectURL(url);
    }
}

function coverageText(geocoded: number, total: number): string {
    if (total === 0) {
        return '0 / 0';
    }
    const percentage = Math.round((geocoded / total) * 100);
    return `${geocoded} / ${total} (${percentage}%)`;
}
